import React, { useCallback, useState } from 'react';
import { Box, Button, IconButton, Input, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import '@fontsource/rubik';

import AuthLayout from '../layouts/AuthLayout';
import AllOrganizationsCard from './components/AllOrganizationsCard';
import KanbanBoard from './components/KanbanBoard';
import EventTypeFilter from './components/EventTypeFilter';
import DeleteConfirmationPopup from './components/DeleteConfirmationPopup';
import DeleteDeclinePopup from './components/DeleteDeclinePopup';
import InviteOrganisationMembersPopup from './components/InviteOrganisationMembersPopup';
import RemoveOrganisationMemberConfirmationPopup from './components/RemoveOrganisationMemberConfirmationPopup';
import AssignAdminRolePopup from './components/AssignAdminRolePopup';
import ViewOrganisationMembersPopup from './components/ViewOrganisationMembersPopup';
import ChangeMemberRoleConfirmationPopup from './components/ChangeMemberRoleConfirmationPopup';
import EditingNotStartedPopup from './components/EditingNotStartedPopup';
import DeclineEditProjectPopup from './components/DeclineEditProjectPopup';
import TeamCreationDrawer from '../teams';
import DeleteTeamPopup from '../teams/components/DeleteTeamPopup';
import RenameTeamPopup from '../teams/components/RenameTeamPopup';
import ViewProjectMembersPopup from '../events/components/ViewProjectMembersPopup';

import { updateState } from '../../redux/commonSlice';
import { updateCreationState } from '../../redux/eventCreationSlice';
import { updateReviewState, resetReviewState } from '../../redux/reviewSlice';
import { resetProjectState, updateProjectState } from '../../redux/projectSlice';
import { updatePermissionState } from '../../redux/permissionSlice';
import { setSnackbar } from '../../redux/messageSlice';

import useDeleteProject from './hooks/useDeleteProject';
import useDashboardActions from './useDashboardActions';
import useCommonActions from '../../commonactions/useCommonActions';
import useDebounce from './hooks/useDebounce';
import { useFillViewportHeight } from '../../hooks/useFillViewportHeight';

import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewToggle from './components/ViewToggle';
import luciLogo from '../../assets/luciLogo2.png';
import { eventSortOptions } from '../../constants/staticData';
import { useEffect } from 'react';
import CustomUserCard from '../events/components/CustomUserCard';
import useCustomTheme from '../../theme/useCustomTheme';

const KanbanPage = () => {
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const dropdownReducer = useSelector((state) => state.root.dropdownReducer);
  const teamsReducer = useSelector((state) => state.root.teamsReducer);
  const createEventReducer = useSelector((state) => state.createEventReducer);
  const projectReducer = useSelector((state) => state.projectReducer);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobileView = useMediaQuery('(max-width:599px)');
  const [theme] = useCustomTheme();

  const [searchString, setSearchString] = useState('');
  const [selectedProjectTypeValue, setSelectedProjectTypeValue] = useState(['View All']);
  const [selectedProjectTypeIds, setSelectedProjectTypeIds] = useState([]);

  const [selectedOrganisation, setSelectedOrganisation] = useState(() => {
    try {
      const saved = sessionStorage.getItem('dashboard_selected_org');
      return saved ? JSON.parse(saved) : '';
    } catch {
      return '';
    }
  });

  const [boardContainerRef, boardContainerHeight] = useFillViewportHeight(16, [isMobileView, selectedOrganisation?.id, selectedOrganisation?.name]);

  const debouncedSearch = useDebounce(searchString, 500);
  const [deleteProject, deleteEventStatus] = useDeleteProject(commonReducer.production_event_id);
  const { acceptMemberInvitation, getOrganisationMembersList } = useDashboardActions();
  const { getUserRoles, getRoles } = useCommonActions();

  // Persist selected org (shared with list view)
  useEffect(() => {
    try {
      if (selectedOrganisation) {
        sessionStorage.setItem('dashboard_selected_org', JSON.stringify(selectedOrganisation));
      } else {
        sessionStorage.removeItem('dashboard_selected_org');
      }
    } catch {}
  }, [selectedOrganisation]);

  useEffect(() => {
    dispatch(resetProjectState());
    getUserRoles({ scope: 'Organization' });
    getOrganisationMembersList();
    getRoles();
    dispatch(updateState({ production_event_id: null, can_upload: false }));
    dispatch(updateReviewState({ open_request_changes_popup: false, open_video_accept_popup: false }));
    dispatch(resetReviewState());
  }, []);

  useEffect(() => {
    dispatch(
      updatePermissionState({
        org_role_id: teamsReducer?.organization_members?.find(
          (mem) => mem.id === commonReducer?.user_id
        )?.role?.id,
      })
    );
  }, [teamsReducer?.organization_members]);

  // Same click handler as Dashboard list view
  const onEventClick = useCallback(
    (event) => {
      dispatch(
        setSnackbar({ type: 'error', flag: false, message: 'You do not have permission to access this project currently' })
      );
      if (
        (commonReducer?.role_name === 'QC' || commonReducer?.role_name === 'Editor') &&
        event.status_type_order < 1
      ) {
        // Pre-editing phase — KanbanCard already blocks click and shows tooltip
      } else {
        const canUpload = event?.flag?.can_upload ?? event?.can_upload;
        dispatch(updateState({ production_event_id: event?.id, can_upload: canUpload }));
        dispatch(updateReviewState({ can_upload: canUpload }));
        dispatch(updateProjectState({ event_tags: Array.isArray(event?.tags) ? event.tags : [], event_video_length: event?.video_length ?? null, event_video_orientation: event?.video_orientation ?? null }));
        navigate(`/completedevent/${event?.organisation_id}/${event?.id}`);
      }
    },
    [commonReducer?.role_name, dispatch, navigate]
  );

  return (
    <AuthLayout sx={{ padding: '2% 3.2% 2.1% 2.2%' }}>
      {/* Top bar */}
      <Box
        gap={2}
        className="flex row a-center"
        sx={{ px: { xs: 1, sm: 3 }, justifyContent: 'space-between', pt: isMobileView && 1.5 }}
      >
        <Box textAlign="center" sx={{ width: { xs: '20%', md: '11%' } }}>
          <img
            src={luciLogo}
            width="100%"
            alt="Luci logo"
            onClick={() => dispatch(updateState({ open_drawer_org_list: true }))}
            style={{ cursor: 'pointer' }}
          />
        </Box>
        <CustomUserCard />
      </Box>

      <Box className="flex row justify-center align-center">
        {/* Left sidebar — org selector */}
        <Box
          id="left"
          className="flex col"
          sx={{
            maxWidth: { xs: '160px', sm: '28%', md: '23%', lg: '21%', xl: '22%' },
            marginTop: '15px',
            height: { xs: '80vh', md: '85vh', lg: '82vh' },
          }}
        >
          <AllOrganizationsCard
            setSelectedOrganisation={setSelectedOrganisation}
            selectedOrganisation={selectedOrganisation}
          />
        </Box>

        {/* Right — board area */}
        <Box id="right" width="100%" minWidth="300px" ml={!isMobileView ? 4.5 : 2.5}>
          {/* Org name + back button */}
          {selectedOrganisation?.name && (
            <Box
              sx={{
                marginTop: { xs: '15px', md: '30px', lg: '35px', xl: '35px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Button
                size="small"
                variant="text"
                startIcon={
                  <ArrowBackIcon
                    sx={{
                      fontSize: { xs: '15px !important', md: '17px !important', lg: '20px !important', xl: '24px !important' },
                      fontWeight: 900,
                      strokeWidth: 2,
                      stroke: 'currentColor',
                    }}
                  />
                }
                onClick={() => setSelectedOrganisation('')}
                sx={{
                  textTransform: 'none',
                  fontSize: { xs: '12px', sm: '13px', md: '14px', lg: '15px', xl: '18px' },
                  fontFamily: 'Rubik',
                  fontWeight: 600,
                  borderRadius: '6px',
                  py: { xs: 0.3, xl: 0.6 },
                  px: { xs: 1, xl: 1.5 },
                  color: 'rgba(0,0,0,0.65)',
                  alignSelf: 'flex-start',
                  '&:hover': { color: 'primary.main', backgroundColor: 'transparent' },
                }}
              >
                All Projects
              </Button>
              <Typography
                sx={{
                  fontSize: { xs: '14px', sm: '14px', md: '16px', lg: '20px', xl: '24px' },
                  fontWeight: 600,
                  letterSpacing: 0,
                  fontFamily: 'Rubik',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {selectedOrganisation?.name}
              </Typography>
            </Box>
          )}

          {/* Toolbar row — search + filters + list-view toggle */}
          <Box
            width="100%"
            className="flex row j-between"
            sx={{ marginTop: '10px', marginBottom: { xs: '12px', md: '15px', lg: '20px', xl: '30px' } }}
          >
            <Box id="search" sx={{ flex: 1 }}>
              <Input
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                sx={{
                  borderBottom: 2,
                  borderColor: '#333333',
                  input: {
                    fontSize: { xs: '12px', md: '13px', lg: '14px', xl: '20px' },
                    fontWeight: 400,
                    color: '#000000',
                  },
                }}
                placeholder="Search"
                startAdornment={
                  <IconButton
                    sx={{
                      paddingBottom: 1,
                      fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' },
                      '& .MuiSvgIcon-root': { fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' } },
                      paddingRight: 2,
                    }}
                  >
                    <SearchIcon style={{ color: '#000000' }} />
                  </IconButton>
                }
              />
            </Box>

            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.7, md: 0.9, lg: 1.2, xl: 1.4 }, pr: 1 }}
            >
              <EventTypeFilter
                sortByOptions={eventSortOptions}
                selectedProjectTypeValue={selectedProjectTypeValue}
                setSelectedProjectTypeValue={setSelectedProjectTypeValue}
                setSelectedProjectTypeIds={setSelectedProjectTypeIds}
                selectedProjectTypeIds={selectedProjectTypeIds}
              />

              <ViewToggle active="board" />
            </Box>
          </Box>

          {/* Hint banner shown when no org is selected — board columns are still visible */}
          {!selectedOrganisation?.id && (
            <Box
              sx={{
                mb: 1.5,
                px: 2,
                py: 1,
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderRadius: '8px',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '11px', lg: '12px' },
                  color: '#888',
                  fontFamily: 'Rubik',
                }}
              >
                Select an organisation from the left panel to load projects
              </Typography>
            </Box>
          )}

          {/* Kanban Board — always visible. Height is measured to fill exactly to the
              bottom of the viewport (minus a small margin), so columns stay capped in
              view and scroll internally instead of a guessed vh that overflows or gaps.
              Bounded on mobile too, since below 600px KanbanBoard shows either a single
              tab-selected column (needs a fixed height for its own scroll/lazy-load) or
              the "All" tab stacking every column — overflow:auto lets that stack scroll
              within this box rather than being clipped. */}
          <Box
            id="kanban-board-container"
            ref={boardContainerRef}
            sx={{
              height: boardContainerHeight ? `${boardContainerHeight}px` : '60vh',
              overflow: 'auto',
            }}
          >
            <KanbanBoard
              orgId={selectedOrganisation?.id}
              onCardClick={onEventClick}
            />
          </Box>
        </Box>
      </Box>

      {/* ---- Popups (same set as Dashboard list view) ---- */}
      {commonReducer.event_delete_popup && (
        <DeleteConfirmationPopup deleteProject={deleteProject} status={deleteEventStatus} />
      )}
      {commonReducer.delete_decline_popup && <DeleteDeclinePopup />}
      {createEventReducer.edit_decline_popup && <DeclineEditProjectPopup />}
      {teamsReducer.open_create_team_form && <TeamCreationDrawer />}
      {teamsReducer.delete_team_popup && <DeleteTeamPopup />}
      {teamsReducer.rename_team_popup && <RenameTeamPopup />}
      {commonReducer.invite_org_members_popup && (
        <InviteOrganisationMembersPopup options={dropdownReducer?.organisation_roles} />
      )}
      {teamsReducer.remove_member_confirmation_popup && (
        <RemoveOrganisationMemberConfirmationPopup />
      )}
      {commonReducer?.change_member_role_confirmation_popup && (
        <ChangeMemberRoleConfirmationPopup />
      )}
      {teamsReducer.assign_role_popup && <AssignAdminRolePopup />}
      {commonReducer.view_members_popup && (
        <ViewOrganisationMembersPopup options={dropdownReducer?.organisation_roles} />
      )}
      {projectReducer.view_project_members_popup && <ViewProjectMembersPopup />}
      {commonReducer?.editing_not_started_popup && <EditingNotStartedPopup />}
    </AuthLayout>
  );
};

export default KanbanPage;
